import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, Wallet, Gift, Users, Calendar,
  Star, User, Plus, BookOpen, CheckCircle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import MarkFulfilledModal from '../../components/MarkFulfilledModal';
import {
  DashboardShell,
  StatsCard,
  ApplicationsTab,
  PurchasedContactsTab,
  MeetingsTab,
  WalletTab,
  ReferralsTab,
} from '../../components/dashboard';
import {
  getMyTutorProfile,
  getWalletBalance,
  getWalletTransactions,
  getReferralStats,
  getMyMeetings,
  getTuitionPosts,
  getMyPurchasedContacts,
  getMyApplications
} from '../../services/authApi';

export const TutorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [referralStats, setReferralStats] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [purchasedContacts, setPurchasedContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fulfilledModal, setFulfilledModal] = useState({ open: false, type: '', id: '', title: '' });

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      try { const p = await getMyTutorProfile(); setProfile(p); } catch { }
      const w = await getWalletBalance(); setWalletBalance(w.balance);
      try { const r = await getReferralStats(); setReferralStats(r); } catch { }
      try { const m = await getMyMeetings(); setMeetings(m.meetings || []); } catch { }
      try { const j = await getTuitionPosts(); setAvailableJobs(j.posts?.slice(0, 5) || []); } catch { }
      try { const c = await getMyPurchasedContacts('all'); setPurchasedContacts(c.purchases || []); } catch { }
      try { const a = await getMyApplications(); setApplications(a.applications || []); } catch { }
      try { const tx = await getWalletTransactions(); setWalletTransactions(tx.transactions || []); } catch { }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <DashboardShell
      title="Tutor Dashboard"
      subtitle={user.email}
      icon={GraduationCap}
      iconBg="bg-purple-600"
      onLogout={handleLogout}
    >
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <StatsCard title="Wallet Balance" value={`${walletBalance} coins`} subtitle="Use to apply for jobs" icon={Wallet} iconColor="text-purple-600" />
        {profile && (
          <StatsCard title="Rating" value={profile.rating || 0} subtitle={`${profile.total_students || 0} students taught`} icon={Star} iconColor="text-yellow-600" />
        )}
        {referralStats && (
          <StatsCard title="Referrals" value={referralStats.total_referrals} subtitle={`${referralStats.total_coins_earned} coins earned`} icon={Gift} iconColor="text-green-600" />
        )}
        <StatsCard title="Meetings" value={meetings.length} subtitle="Scheduled sessions" icon={Calendar} iconColor="text-orange-600" />
      </div>

      {/* Profile Check */}
      {!profile && (
        <Card className="mb-8 border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="h-5 w-5 text-purple-600" />
              <span>Complete Your Profile</span>
            </CardTitle>
            <CardDescription>
              Create your tutor profile to start applying for jobs and connecting with students
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/profile/create')} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="h-4 w-4 mr-2" /> Create Profile
            </Button>
          </CardContent>
        </Card>
      )}

      {profile && profile.review_status !== 'approved' && (
        <Card className="mb-8 border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-yellow-800">Profile Under Review</CardTitle>
            <CardDescription className="text-yellow-700">
              Your profile is being reviewed by our team. You'll be notified once it's verified.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="opportunities">Job Opportunities</TabsTrigger>
          <TabsTrigger value="applications">Applications ({applications.length})</TabsTrigger>
          <TabsTrigger value="purchased">Purchased ({purchasedContacts.length})</TabsTrigger>
          <TabsTrigger value="meetings">Meetings</TabsTrigger>
          <TabsTrigger value="wallet">Wallet</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          {profile && <TabsTrigger value="profile">Profile</TabsTrigger>}
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4">
              <Button onClick={() => navigate('/find-job')} variant="outline" className="h-24 flex-col">
                <BookOpen className="h-8 w-8 mb-2 text-purple-600" /><span>Browse Jobs</span>
              </Button>
              <Button onClick={() => navigate('/tuition-posts')} variant="outline" className="h-24 flex-col">
                <Users className="h-8 w-8 mb-2 text-blue-600" /><span>View Requirements</span>
              </Button>
              <Button onClick={() => navigate('/profile/edit')} variant="outline" className="h-24 flex-col">
                <User className="h-8 w-8 mb-2 text-green-600" /><span>Update Profile</span>
              </Button>
            </CardContent>
          </Card>
          {profile && (
            <Card>
              <CardHeader><CardTitle>Your Teaching Profile</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><p className="text-sm text-gray-600">Full Name</p><p className="font-medium">{profile.full_name}</p></div>
                  <div><p className="text-sm text-gray-600">Experience</p><p className="font-medium">{profile.experience_years} years</p></div>
                  <div><p className="text-sm text-gray-600">Hourly Rate</p><p className="font-medium">₹{profile.hourly_rate}/hr</p></div>
                  <div><p className="text-sm text-gray-600">Subjects</p><p className="font-medium">{profile.subjects?.join(', ')}</p></div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Opportunities */}
        <TabsContent value="opportunities">
          <Card>
            <CardHeader>
              <CardTitle>Available Student Requirements</CardTitle>
              <CardDescription>Apply for tuition opportunities that match your expertise</CardDescription>
            </CardHeader>
            <CardContent>
              {availableJobs.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No opportunities available at the moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {availableJobs.map((job) => (
                    <div key={job.id} className="border rounded-lg p-4 hover:border-purple-300 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold">{job.category}</h3>
                        <Badge>{job.budget_type === 'hourly' ? `₹${job.budget_amount}/hr` : `₹${job.budget_amount}/mo`}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{job.description}</p>
                      <div className="flex flex-wrap gap-2 text-xs mb-3">
                        {job.subjects?.map((subject, idx) => (
                          <span key={idx} className="bg-purple-50 text-purple-700 px-2 py-1 rounded">{subject}</span>
                        ))}
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-600">
                          <span>{job.city}</span><span className="mx-2">•</span><span>{job.class_mode}</span>
                        </div>
                        <Button size="sm" className="bg-purple-600 hover:bg-purple-700">Apply (10 coins)</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shared Tabs */}
        <TabsContent value="applications"><ApplicationsTab applications={applications} /></TabsContent>
        <TabsContent value="purchased"><PurchasedContactsTab purchasedContacts={purchasedContacts} /></TabsContent>
        <TabsContent value="meetings"><MeetingsTab meetings={meetings} /></TabsContent>
        <TabsContent value="wallet"><WalletTab walletBalance={walletBalance} walletTransactions={walletTransactions} onNavigateWallet={() => navigate('/wallet')} /></TabsContent>
        <TabsContent value="referrals"><ReferralsTab referralStats={referralStats} /></TabsContent>

        {/* Profile Tab */}
        {profile && (
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Tutor Profile</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => navigate('/profile/edit')}>Edit Profile</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-4">Basic Information</h3>
                    <div className="space-y-3">
                      <div><p className="text-sm text-gray-600">Full Name</p><p className="font-medium">{profile.full_name}</p></div>
                      <div><p className="text-sm text-gray-600">Username</p><p className="font-medium">{profile.username}</p></div>
                      <div><p className="text-sm text-gray-600">Qualification</p><p className="font-medium">{profile.qualification}</p></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-4">Teaching Details</h3>
                    <div className="space-y-3">
                      <div><p className="text-sm text-gray-600">Experience</p><p className="font-medium">{profile.experience_years} years</p></div>
                      <div><p className="text-sm text-gray-600">Hourly Rate</p><p className="font-medium">₹{profile.hourly_rate}/hour</p></div>
                      <div><p className="text-sm text-gray-600">Teaching Mode</p><p className="font-medium">{profile.teaching_mode?.join(', ')}</p></div>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <h3 className="font-semibold mb-4">Subjects</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.subjects?.map((subject, idx) => (
                        <Badge key={idx} variant="secondary">{subject}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <h3 className="font-semibold mb-4">Bio</h3>
                    <p className="text-gray-700">{profile.bio}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Mark as Fulfilled Modal */}
      <MarkFulfilledModal
        isOpen={fulfilledModal.open}
        onClose={() => setFulfilledModal({ open: false, type: '', id: '', title: '' })}
        contentType={fulfilledModal.type}
        contentId={fulfilledModal.id}
        contentTitle={fulfilledModal.title}
        token={user?.token}
        onSuccess={() => {
          getMyPurchasedContacts(user.token)
            .then(data => setPurchasedContacts(data))
            .catch(err => console.error('Error refreshing contacts:', err));
        }}
      />
    </DashboardShell>
  );
};
